import { AlertTriangle, Clock } from "lucide-react";

import { Suggerimento } from "@/components/panoramica/Suggerimento";
import type { CanalePanoramica } from "@/lib/api";
import { freschezzaPct, oraBreve, statoCanale } from "@/lib/panoramica";
import { cn } from "@/lib/utils";

interface CellaCanaleProps {
  canale: CanalePanoramica;
  adesso: Date;
}

export function CellaCanale({ canale, adesso }: CellaCanaleProps): JSX.Element {
  const kpi = canale.kpi;
  const stato = statoCanale(canale, adesso);
  const fresca = kpi ? freschezzaPct(kpi) : null;
  return (
    <div className="flex min-w-0 flex-col justify-center gap-[3px] py-1.5 pl-3.5 pr-1.5">
      <span className="truncate text-data font-semibold text-ink">{canale.label}</span>
      <span className="flex items-center gap-1.5">
        <span className="font-mono text-micro font-medium text-ink-3">{canale.country}</span>
        {kpi ? (
          <span className={cn("pill", kpi.auto_acceso ? "pill-ok" : "pill-neutral")}>
            {kpi.auto_acceso ? "Auto on" : "Auto off"}
          </span>
        ) : null}
        {fresca !== null ? (
          <Suggerimento
            titolo="Freschezza"
            testo={`Prezzi controllati nelle ultime 24 h: ${fresca}%. Il resto è più vecchio di un giorno.`}
            allinea="sinistra"
          >
            <span
              tabIndex={0}
              className="flex cursor-help items-center gap-1 rounded-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            >
              <span className={cn("dot", fresca >= 90 ? "dot-ok" : fresca >= 60 ? "dot-warn" : "dot-err")} aria-hidden="true" />
              <span className="font-mono text-micro tabular-nums text-ink-3">{fresca}%</span>
            </span>
          </Suggerimento>
        ) : null}
      </span>
      {stato === "vecchio" && canale.calcolato_at ? (
        <Suggerimento
          titolo="Numeri vecchi"
          testo={`I numeri di ${canale.label} sono delle ${oraBreve(canale.calcolato_at)}. Il prossimo ricalcolo li aggiorna.`}
          allinea="sinistra"
        >
          <span
            tabIndex={0}
            className="flex cursor-help items-center gap-1 font-mono text-micro text-ink-2 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            <Clock className="h-[11px] w-[11px] text-warn" strokeWidth={1.9} aria-hidden="true" />
            dati alle {oraBreve(canale.calcolato_at)}
          </span>
        </Suggerimento>
      ) : null}
      {stato === "errore" && canale.ultimo_errore_at ? (
        <Suggerimento
          titolo={`Ricalcolo fallito alle ${oraBreve(canale.ultimo_errore_at)}`}
          testo={`${canale.ultimo_errore ?? "Errore sconosciuto"}.${
            canale.calcolato_at ? ` I numeri mostrati sono delle ${oraBreve(canale.calcolato_at)}.` : ""
          }`}
          allinea="sinistra"
        >
          <span
            tabIndex={0}
            className="flex cursor-help items-center gap-1 font-mono text-micro text-danger focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            <AlertTriangle className="h-[11px] w-[11px]" strokeWidth={1.9} aria-hidden="true" />
            ricalcolo fallito {oraBreve(canale.ultimo_errore_at)}
          </span>
        </Suggerimento>
      ) : null}
    </div>
  );
}
